import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { StudioProvider } from 'Contexts/StudioContext'
import { Studio } from 'Core/Studio'
import {
    getJobHierarchy,
    getJobOutputs,
    getJobPreview,
    getTaskDetails,
    getWorkflowStepsByJobId,
} from 'Services/tasks.service'
import { PAGE_TITLES } from 'Constants/page.constants'
import { ContextType, TaskMode } from 'Types/Tasks.types'
import { useTitle } from 'Hooks/use-title'
import { useMessage } from 'shared/hooks/use-message'
import { useQuery } from '@tanstack/react-query'
import { Loader } from 'Components/common/Loader/Loader'
import { EmptyStateIndicator } from 'Components/common/EmptyStaleIndicator/EmptyStaleIndicator'
import ErrorIcon from 'Assets/icons/error-icon.svg'
import Flex from 'antd/lib/flex'

export const PreviewPage = () => {
    const [studio, setStudio] = useState<Studio>()
    const { jobId: paramJobId } = useParams()
    const [messageApi] = useMessage()
    const navigate = useNavigate()

    useTitle(PAGE_TITLES.PREVIEW)

    const location = useLocation()
    const [searchParams] = useSearchParams()
    const childJobIdParam = searchParams.get('childJobId')

    const {
        jobId: locationJobId,
        stepId: locationStepId,
        taskId: locationTaskId,
    } = location.state ?? {}

    const {
        data: jobHierarchy,
        isLoading: isJobHierarchyLoading,
        error: jobHierarchyError,
    } = useQuery({
        queryKey: ['job-hierarchy', paramJobId],
        queryFn: () => getJobHierarchy(paramJobId as string),
    })

    const selectedJobId =
        childJobIdParam || locationJobId || jobHierarchy?.child_jobs?.[0]?.id || paramJobId
    const isNotChildJob = !!jobHierarchy && !jobHierarchy.parent_job_id

    // as workflow steps will be same for a parent job and its child jobs so we can rely on paramJobId
    const {
        data: workflowSteps,
        isLoading: isWorkflowStepsLoading,
        error: workflowStepsError,
    } = useQuery({
        queryKey: ['workflow-steps', paramJobId],
        queryFn: () => getWorkflowStepsByJobId(paramJobId as string),
        enabled: isNotChildJob,
    })

    // job outputs will vary for child jobs so we can rely on selectedJobId
    const {
        data: jobOutputs,
        isLoading: isJobOutputsLoading,
        error: jobOutputsError,
    } = useQuery({
        queryKey: ['job-outputs', selectedJobId],
        queryFn: () => getJobOutputs(selectedJobId as string),
        enabled: isNotChildJob && !!selectedJobId,
    })

    const selectedTaskId = locationStepId
        ? undefined
        : locationTaskId || jobOutputs?.taskOutput?.[jobOutputs.taskOutput.length - 1]?.taskId

    const {
        data: taskDetails,
        isLoading: isTaskDetailsLoading,
        error: taskDetailsError,
    } = useQuery({
        queryKey: ['task-details', selectedTaskId],
        queryFn: () => getTaskDetails(selectedTaskId as string),
        enabled: isNotChildJob && !!selectedTaskId,
        select: data => {
            if (data && jobOutputs) {
                data.assignmentResp.assignment.output_history = jobOutputs.taskOutput.map(task => ({
                    task_id: task.taskId,
                    is_current_user_step: false,
                    result: task.result,
                    step_type: task.stepType,
                    step_name: task.stepName,
                    output: task.output,
                }))
            }
            return data
        },
    })

    const selectedStepId =
        locationStepId || (selectedTaskId ? undefined : workflowSteps?.step_details?.[0]?.id)

    const {
        data: jobPreview,
        isLoading: isJobPreviewLoading,
        error: jobPreviewError,
    } = useQuery({
        queryKey: ['job-preview', selectedJobId, selectedStepId],
        queryFn: () => getJobPreview(selectedJobId as string, selectedStepId as string),
        enabled: isNotChildJob && !selectedTaskId && !!selectedStepId,
    })

    useEffect(() => {
        if (jobHierarchy?.parent_job_id) {
            const params = new URLSearchParams({ childJobId: paramJobId as string })
            navigate(`/preview/${jobHierarchy.parent_job_id}?${params.toString()}`, {
                state: {},
                replace: true,
            })
        }
    }, [jobHierarchy?.parent_job_id, navigate, paramJobId])

    useEffect(() => {
        studio?.destroy()
        setStudio(undefined)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedJobId, selectedTaskId, selectedStepId])

    useEffect(() => {
        if (jobHierarchyError) {
            messageApi.error('Failed to load job hierarchy')
            console.error('Job hierarchy error:', jobHierarchyError)
        }
        if (workflowStepsError) {
            messageApi.error('Failed to load workflow steps')
            console.error('Workflow steps error:', workflowStepsError)
        }
        if (jobOutputsError) {
            messageApi.error('Failed to load job outputs')
            console.error('Job outputs error:', jobOutputsError)
        }
        if (taskDetailsError) {
            messageApi.error('Failed to load task details')
            console.error('Task details error:', taskDetailsError)
        }
        if (jobPreviewError) {
            messageApi.error('Failed to load job preview')
            console.error('Job preview error:', jobPreviewError)
        }
    }, [
        jobHierarchyError,
        workflowStepsError,
        jobOutputsError,
        taskDetailsError,
        jobPreviewError,
        messageApi,
    ])

    useEffect(() => {
        // Use taskDetails if a task is selected, otherwise use jobPreview
        const previewData = taskDetails || jobPreview

        if (!jobOutputs || !previewData || !workflowSteps || !jobHierarchy || studio) {
            return
        }

        try {
            const newStudio = new Studio({
                taskType: previewData?.assignmentResp?.assignment.task.type,
                interfaceKey: previewData?.assignmentResp?.assignment.interface_key,
                taskConfig: {
                    jobId: paramJobId,
                    steps: workflowSteps?.step_details,
                    childJobs: jobHierarchy?.child_jobs,
                    outputs: jobOutputs,
                    assignment: previewData?.assignmentResp?.assignment,
                    //We won't be making a submission for Viewer Mode
                    submissionId: 'MockSubmissionId',
                    mode: TaskMode.Viewer,
                },
                context: {
                    type: ContextType.NormalTask,
                },
                pluginsConfig: [],
                onNextTask: () => {},
                onExitTask: () => {},
            })
            setStudio(newStudio)
        } catch (error) {
            messageApi.error('Error initializing preview')
            console.error('Error initializing preview:', error)
        }
    }, [
        taskDetails,
        jobPreview,
        workflowSteps,
        selectedTaskId,
        jobHierarchy,
        jobOutputs,
        paramJobId,
        messageApi,
        studio,
    ])

    const isLoading =
        isJobHierarchyLoading ||
        isWorkflowStepsLoading ||
        isJobOutputsLoading ||
        isJobPreviewLoading ||
        isTaskDetailsLoading

    const hasCriticalError =
        jobHierarchyError ||
        workflowStepsError ||
        taskDetailsError ||
        jobPreviewError ||
        jobOutputsError

    if (hasCriticalError) {
        return (
            <Flex vertical style={{ height: '80vh' }} gap={10} align="center" justify="center">
                <EmptyStateIndicator
                    label="Unable to load preview"
                    caption={'An error occurred while loading the preview. Please try again.'}
                    icon={ErrorIcon}
                />
            </Flex>
        )
    }

    if (isLoading || !studio) {
        return <Loader title="Loading preview..." />
    }

    return (
        <div data-testid="preview-wrapper">
            <StudioProvider value={studio}>{studio.workbench.element}</StudioProvider>
        </div>
    )
}
